import { useState, useEffect } from "react";
import axios from "axios";
import { Linkedin, ArrowUpRight, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import useSEO from "@/lib/useSEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { CONTACT } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LinkedInCTA = ({ testId }) => (
  <a href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer" className="btn-solid"
     style={{ background: "#B39A62", borderColor: "#B39A62", color: "#17263D" }} data-testid={testId}>
    <Linkedin size={16} strokeWidth={1.6} /> Follow us on LinkedIn <ArrowUpRight size={15} strokeWidth={1.6} />
  </a>
);

const paras = (body) => (body || "").split(/\n{2,}|\n/).map((s) => s.trim()).filter(Boolean);

const Reader = ({ article, onBack }) => (
  <section className="py-16 lg:py-24" data-testid="insight-reader">
    <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-16">
      <button onClick={onBack} className="arrow-link mb-8" data-testid="insight-back">
        <ArrowLeft size={15} strokeWidth={1.5} /> <span className="link-underline">All Insights</span>
      </button>
      <div className="overline">{article.category} · {article.date}</div>
      <h1 className="font-serif text-navy text-4xl lg:text-5xl leading-[1.1] tracking-tight mt-4">{article.title}</h1>
      <div className="h-px w-20 bg-brass/60 mt-6" />
      {article.cover_image ? (
        <div className="relative overflow-hidden mt-8" style={{ aspectRatio: "16 / 9" }}>
          <img src={article.cover_image} alt={article.title} className="img-cover" loading="lazy" />
        </div>
      ) : null}
      <div className="mt-8 space-y-5">
        {paras(article.body).map((p, i) => (
          <p key={i} className="text-base lg:text-lg leading-[1.7] text-charcoal/80">{p}</p>
        ))}
      </div>
    </div>
  </section>
);

const Insights = () => {
  useSEO({
    title: "Insights",
    description: "Selected perspectives from Arun Gupta & Co. on tax and regulatory matters, accounting and financial reporting, business and finance, and advisory topics.",
    path: "/insights",
  });

  const [data, setData] = useState({ articles: [], linkedin: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/insights`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const hasArticles = data.articles.length > 0;
  const hasPosts = data.linkedin.length > 0;

  return (
    <>
      <PageHero
        eyebrow="Insights"
        titleLines={["Selected perspectives", "from our practice."]}
        intro="We regularly share professional updates, observations and perspectives on LinkedIn. A selection of our recent posts and insights is featured here."
        testId="insights-hero"
      />

      {loading ? (
        <div className="py-24 text-center text-charcoal/40"><Loader2 className="animate-spin inline" /></div>
      ) : selected ? (
        <Reader article={selected} onBack={() => setSelected(null)} />
      ) : (
        <>
          {hasArticles && (
            <section className="py-16 lg:py-24" data-testid="insights-articles">
              <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
                <Reveal><div className="overline">Written Insights</div></Reveal>
                <div className="mt-10 border-t border-border-subtle">
                  {data.articles.map((a) => {
                    const readable = paras(a.body).length > 0;
                    return (
                      <Reveal key={a.id}>
                        <article className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 py-10 border-b border-border-subtle items-start" data-testid="insight-article">
                          {a.cover_image ? (
                            <div className="lg:col-span-4">
                              <div className="relative overflow-hidden" style={{ aspectRatio: "4 / 3" }}>
                                <img src={a.cover_image} alt={a.title} className="img-cover" loading="lazy" />
                              </div>
                            </div>
                          ) : null}
                          <div className={a.cover_image ? "lg:col-span-8" : "lg:col-span-11"}>
                            <div className="overline">{a.category} · {a.date}</div>
                            <h3 className="font-serif text-navy text-2xl lg:text-3xl mt-3 leading-snug">{a.title}</h3>
                            {a.excerpt ? <p className="mt-3 text-base leading-relaxed text-charcoal/75 max-w-2xl">{a.excerpt}</p> : null}
                            {readable && (
                              <button onClick={() => setSelected(a)} className="arrow-link mt-5" data-testid="insight-read">
                                <span className="link-underline">Read</span> <ArrowRight size={15} strokeWidth={1.5} />
                              </button>
                            )}
                          </div>
                        </article>
                      </Reveal>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* LinkedIn selection / gateway */}
          <section className="py-20 lg:py-28" style={{ background: "#17263D" }} data-testid="insights-linkedin">
            <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-5">
                  <Reveal>
                    <div className="overline">On LinkedIn</div>
                    <h2 className="font-serif text-ivory text-3xl sm:text-4xl lg:text-[3rem] tracking-tight mt-5 leading-[1.08]">
                      Perspectives from our desk.
                    </h2>
                    <p className="mt-6 text-base leading-relaxed text-ivory/70 max-w-md">
                      LinkedIn is currently our primary publishing channel. A selection of our recent
                      posts is featured here.
                    </p>
                    <div className="mt-8"><LinkedInCTA testId="insights-linkedin-btn" /></div>
                  </Reveal>
                </div>
                <div className="lg:col-span-6 lg:col-start-7">
                  {hasPosts ? (
                    <div className="border-t" style={{ borderColor: "rgba(247,244,238,0.16)" }}>
                      {data.linkedin.map((p) => (
                        <Reveal key={p.id}>
                          <a href={p.url || CONTACT.linkedin} target="_blank" rel="noopener noreferrer"
                             className="group flex gap-5 py-6 border-b" style={{ borderColor: "rgba(247,244,238,0.16)" }} data-testid="insight-linkedin-post">
                            {p.image ? (
                              <div className="w-24 h-24 shrink-0 overflow-hidden">
                                <img src={p.image} alt={p.title} className="img-cover" loading="lazy" />
                              </div>
                            ) : null}
                            <div className="min-w-0">
                              <div className="overline">{p.category} · {p.date}</div>
                              <h3 className="font-serif text-ivory text-xl mt-2 leading-snug">{p.title}</h3>
                              {p.description ? <p className="mt-2 text-sm text-ivory/65 leading-relaxed">{p.description}</p> : null}
                              <span className="arrow-link mt-3" style={{ color: "#B39A62" }}>
                                <span className="link-underline">View on LinkedIn</span> <ArrowUpRight size={14} strokeWidth={1.5} />
                              </span>
                            </div>
                          </a>
                        </Reveal>
                      ))}
                    </div>
                  ) : (
                    <Reveal>
                      <p className="font-serif italic text-ivory/60 text-xl lg:text-2xl leading-relaxed">
                        {hasArticles
                          ? "Follow the firm on LinkedIn for our latest professional updates and observations."
                          : "Our written insights are being prepared. In the meantime, our current perspectives and updates are shared on LinkedIn."}
                      </p>
                    </Reveal>
                  )}
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
};

export default Insights;
